import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { useGame } from "../context/GameContext";
import PageLayout from "../components/PageLayout";
import MdGlowButton from "../components/MdGlowButton";
import CandidateSilhouetteGuide from "../components/CandidateSilhouetteGuide";
import { CAMERA_CONSTRAINTS } from "../lib/videoCapture";
import {
  CAMERA_GUARD_MESSAGES,
  ensureCameraImageUsable,
  stopMediaStream,
} from "../lib/cameraGuard";
import { loadHtmlToImage } from "../lib/loadHtmlToImage";
import { ensureCompositeFontsReady } from "../lib/compositeFonts";
import "../play.css";
import { Camera, Mic, AlertTriangle, PlayCircle, Loader2 } from "lucide-react";

const CameraSetup = () => {
  const videoRef = useRef(null);
  const [ready, setReady] = useState(false);
  const [checking, setChecking] = useState(false);
  const [error, setError] = useState("");
  const { token, streamRef } = useGame();
  const navigate = useNavigate();

  useEffect(() => {
    if (!token) navigate("/");
  }, [token, navigate]);

  useEffect(() => {
    const el = videoRef.current;
    const stream = streamRef.current;
    if ((ready || checking) && el && stream && el.srcObject !== stream) {
      el.srcObject = stream;
      const p = el.play();
      if (p && typeof p.catch === "function") p.catch(() => {});
    }
  }, [ready, checking, streamRef]);

  const failCamera = (message) => {
    stopMediaStream(streamRef.current);
    streamRef.current = null;
    if (videoRef.current) videoRef.current.srcObject = null;
    setReady(false);
    setError(message);
    toast.error(message);
  };

  const requestCamera = async () => {
    setError("");
    setReady(false);
    setChecking(true);
    stopMediaStream(streamRef.current);
    streamRef.current = null;

    try {
      const stream = await navigator.mediaDevices.getUserMedia(CAMERA_CONSTRAINTS);
      streamRef.current = stream;

      const el = videoRef.current;
      if (el) {
        el.srcObject = stream;
        await el.play().catch(() => {});
      }

      await ensureCameraImageUsable(el, stream);

      setReady(true);
      Promise.all([loadHtmlToImage(), ensureCompositeFontsReady()]).catch(() => {});
    } catch (err) {
      const message =
        err?.code && CAMERA_GUARD_MESSAGES[err.code]
          ? CAMERA_GUARD_MESSAGES[err.code]
          : err?.message && String(err.message).includes("caméra")
            ? err.message
            : "Impossible d'accéder à la caméra. Vérifiez les permissions.";
      failCamera(message);
    } finally {
      setChecking(false);
    }
  };

  const handleMainAction = async () => {
    if (checking) return;
    if (!ready) {
      await requestCamera();
      return;
    }

    setChecking(true);
    setError("");
    try {
      await ensureCameraImageUsable(videoRef.current, streamRef.current, {
        sampleCount: 4,
        sampleIntervalMs: 120,
        timeoutMs: 3000,
      });
      navigate("/play");
    } catch (err) {
      const message =
        err?.code && CAMERA_GUARD_MESSAGES[err.code]
          ? CAMERA_GUARD_MESSAGES[err.code]
          : CAMERA_GUARD_MESSAGES.NO_VIDEO;
      failCamera(message);
    } finally {
      setChecking(false);
    }
  };

  const buttonDisabled = checking;
  const showReady = ready && !checking;

  return (
    <PageLayout>
      <div className="max-w-xl mx-auto flex flex-col gap-8">
        <div className="fade-up text-center">
          <h2 className="text-2xl sm:text-3xl font-bold text-white">Activez votre caméra</h2>
          <p className="mt-4 text-white/60 leading-relaxed">
            Le studio enregistre automatiquement toute votre partie en direct.
          </p>
          <div className="mt-4 mx-auto max-w-lg rounded-xl border border-amber-300/40 bg-amber-300/10 px-4 py-3 text-left text-amber-100">
            <span className="font-bold">Important !</span> Le jeu se joue en binôme. Il est impératif que les 2
            candidats soient visibles en permanence lors de la partie.
          </div>
          <div className="mt-4 mx-auto max-w-lg space-y-3 text-left">
            <div className="md-card p-4 sm:p-5 flex gap-3" data-testid="camera-step-1">
              <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-md-cyan/20 text-md-cyan text-xs font-bold">
                1
              </span>
              <p className="text-sm text-white/80 leading-relaxed pt-0.5 min-w-0">
                Cliquez sur <strong className="text-white">« Activer caméra &amp; micro »</strong>. 2 silhouettes
                apparaissent sur votre retour vidéo.
              </p>
            </div>
            <div className="md-card p-4 sm:p-5 flex gap-3" data-testid="camera-step-2">
              <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-md-cyan/20 text-md-cyan text-xs font-bold">
                2
              </span>
              <p className="text-sm text-white/80 leading-relaxed pt-0.5 min-w-0">
                Poser votre ordinateur sur une table.
              </p>
            </div>
            <div className="md-card p-4 sm:p-5 flex gap-3" data-testid="camera-step-3">
              <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-md-cyan/20 text-md-cyan text-xs font-bold">
                3
              </span>
              <p className="text-sm text-white/80 leading-relaxed pt-0.5 min-w-0">
                Mettez-vous côte à côte de façon à ce que vous soyez tous les 2 visibles dans le retour vidéo.
              </p>
            </div>
            <div className="md-card p-4 sm:p-5 flex gap-3" data-testid="camera-step-4">
              <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-md-cyan/20 text-md-cyan text-xs font-bold">
                4
              </span>
              <p className="text-sm text-white/80 leading-relaxed pt-0.5 min-w-0">
                Ne vous éloignez pas trop afin que votre micro puisse capter vos échanges.
              </p>
            </div>
          </div>
        </div>

        <div className="md-card overflow-hidden border border-md-cyan/15 aspect-video relative fade-up w-full">
          <video
            ref={videoRef}
            autoPlay
            muted
            playsInline
            className={`w-full h-full object-cover play-video-mirror transition-opacity duration-500 ${
              ready || checking ? "opacity-100" : "opacity-0"
            }`}
            data-testid="camera-preview"
          />
          {showReady && <CandidateSilhouetteGuide />}
          {!ready && !checking && (
            <div className="absolute inset-0 flex flex-col items-center justify-center text-white/40">
              <Camera size={72} strokeWidth={1} />
              <div className="mt-4 font-semibold text-lg uppercase tracking-widest">Caméra inactive</div>
            </div>
          )}
          {checking && (
            <div className="absolute inset-0 flex flex-col items-center justify-center bg-black/45 text-white">
              <Loader2 className="h-10 w-10 animate-spin text-md-cyan" />
              <div className="mt-3 text-sm font-medium">Vérification de la caméra…</div>
            </div>
          )}
        </div>

        {error && (
          <div className="fade-up text-sm text-red-400 flex items-center gap-2">
            <AlertTriangle size={16} /> {error}
          </div>
        )}

        <MdGlowButton
          variant={showReady ? "success" : "default"}
          onClick={handleMainAction}
          wrapClassName="mt-0"
          disabled={buttonDisabled}
          data-testid={showReady ? "start-game-button" : "enable-camera-button"}
        >
          {checking ? (
            <>
              <Loader2 className="w-5 h-5 shrink-0 animate-spin" />
              Vérification…
            </>
          ) : showReady ? (
            <>
              <PlayCircle className="w-5 h-5 shrink-0" />
              Je suis prêt — Lancer le jeu
            </>
          ) : (
            <>
              <Camera className="w-5 h-5 shrink-0" />
              Activer caméra & micro
              <Mic className="w-5 h-5 shrink-0" />
            </>
          )}
        </MdGlowButton>

        {showReady && (
          <p className="text-sm text-emerald-400/90 text-center fade-up">
            Caméra OK — appuyez sur le bouton vert pour lancer le jeu.
          </p>
        )}
      </div>
    </PageLayout>
  );
};

export default CameraSetup;
